import styles from './ProtocolsTabPage.module.css';

import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GroupOperations, ProtocolOperations, protocolsStore } from 'store/mobx/protocols.store';
import ListNavigationDrawer from 'widgets/ListNavigationDrawer';
import UpsertGroupModal from './UpsertGroupModal';
import UpsertProtocol from './UpsertProtocol';
import useAlert from 'hooks/useAlert';

const ProtocolsTabPage = observer(({ mode }) => {
  const { showAlert } = useAlert();
  const {
    groups,
    resetGroupProtocols,
    setGroupsAndProtocols,
    setUpdateGroupState,
    setCreateGroupState,
    setOperationMode,
    removeGroup,
    setProtocolsByGroupId,
    updateSelectedProtocol,
    setProtocolOperationMode,
    protocolOperationMode,
    selectedProtocolId,
    setSelected,
    isLoading,
  } = protocolsStore;

  const listNavigationRef = useRef(null);
  const [groupModalOpened, setGroupModalOpened] = useState(false);
  const [upsertProtocolOpened, setUpsertProtocolOpened] = useState(false);

  const actions = [
    {
      name: 'Новую группу',
      callback: () => createGroup(),
    },
    {
      name: 'Новый протокол',
      callback: () => createProtocol(),
    },
  ];

  useEffect(() => {
    setUpsertProtocolOpened(!!selectedProtocolId);
  }, [selectedProtocolId]);

  const createProtocol = useCallback(async () => {
    updateSelectedProtocol({
      id_group: '',
      name: '',
      operations: [],
    });
    setProtocolOperationMode(ProtocolOperations.Add);
    setSelected(null);
    listNavigationRef.current.resetSelected();
  }, []);

  const editGroup = useCallback(async (value) => {
    setOperationMode(GroupOperations.Edit);
    setUpdateGroupState(value);
    setGroupModalOpened(true);
  }, []);

  const createGroup = useCallback(async () => {
    setOperationMode(GroupOperations.Add);
    setCreateGroupState(null);
    setGroupModalOpened(true);
  }, []);

  const deleteGroup = useCallback((value) => {
    sureDeleteProtocolGroupAlert(async () => {
      const result = await removeGroup(value.id);
      if (result.error_code == 2008) return alarmGroupContainsRelatedAlert();
      if (result.error_code) return errorCommonAlert(result.error_code);

      return successGroupDeletedAlert();
    });
  }, []);

  const refreshGroupsAndProtocols = useCallback((expandedGroups, { search = undefined } = {}) => {
    setGroupsAndProtocols(expandedGroups, { search });
  }, []);

  const openAnotherProtocol = (id) => {
    setSelected(id);
  };

  const handleSelectProtocol = async (id) => {
    if (protocolOperationMode == ProtocolOperations.Edit) {
      return alarmUnsavedDataExists(() => {
        setProtocolOperationMode(ProtocolOperations.View);
        openAnotherProtocol(id);
      });
    }
    if (id && protocolOperationMode == ProtocolOperations.Add) {
      return alarmUnsavedDataExists(() => openAnotherProtocol(id));
    }
    openAnotherProtocol(id);
  };

  const alarmUnsavedDataExists = (callback) =>
    showAlert(
      'Вы действительно хотите открыть другой протокол? Все несохраненные данные будут удалены!',
      'sure',
      callback,
    );

  const alarmGroupContainsRelatedAlert = () => showAlert('Группа содержит протоколы!', 'info');

  const errorCommonAlert = (code) =>
    showAlert(`Код № ${code}. Группа не может быть удалена!`, 'error');

  const successGroupDeletedAlert = () => showAlert(`Группа успешно удалена!`, 'success');

  const sureDeleteProtocolGroupAlert = (callback) =>
    showAlert('Вы действительно хотите удалить группу?', 'sure', callback);

  return (
    <div className={styles.pageContainer}>
      <ListNavigationDrawer
        ref={listNavigationRef}
        actionsCallbacks={actions}
        groups={groups}
        isLoading={isLoading}
        loadGroupApi={setProtocolsByGroupId}
        resetGroup={resetGroupProtocols}
        editGroupCallback={editGroup}
        deleteGroupCallback={deleteGroup}
        onUpdate={refreshGroupsAndProtocols}
        onSelect={handleSelectProtocol}
      >
        {(upsertProtocolOpened || protocolOperationMode === ProtocolOperations.Add) && (
          <UpsertProtocol />
        )}
      </ListNavigationDrawer>
      {groupModalOpened && <UpsertGroupModal setModalOpened={setGroupModalOpened} mode={mode} />}
    </div>
  );
});

export default ProtocolsTabPage;
